/**
 * MicButton — dictado por voz (2026-07-17).
 *
 * Usa el reconocimiento de voz del propio navegador (Web Speech API), sin
 * backend: al pulsar, escucha y transcribe en español; cada frase reconocida
 * se envía a `onText(texto)` para que el campo la vaya rellenando. Vuelve a
 * pulsar para parar.
 *
 * Requiere HTTPS (lo tenemos) y permiso de micrófono. En navegadores sin
 * soporte (algunos móviles/Firefox), el botón no se muestra.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { toast } from 'sonner';

const SpeechRecognition =
  typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

export function isSpeechSupported() {
  return !!SpeechRecognition;
}

export default function MicButton({ onText, className = '', title = 'Dictar por voz', lang = 'es-ES' }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.stop(); } catch (_) { /* noop */ } }, []);

  if (!SpeechRecognition) return null;

  const toggle = () => {
    if (listening) {
      try { recRef.current?.stop(); } catch (_) { /* noop */ }
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = lang;
    rec.interimResults = false;
    rec.continuous = true;
    rec.onresult = (e) => {
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      const t = finalText.trim();
      if (t) onText?.(t);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        toast.error('Permite el micrófono en el navegador para poder dictar.');
      } else if (e.error !== 'aborted' && e.error !== 'no-speech') {
        toast.error('No se pudo usar el micrófono.');
      }
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={listening ? 'Detener dictado' : title}
      aria-label={title}
      data-testid="mic-button"
      className={`inline-flex items-center justify-center rounded-md transition-colors ${
        listening
          ? 'bg-red-500 text-white animate-pulse'
          : 'text-muted-foreground hover:text-[#2E6E5E] hover:bg-[rgba(46,110,94,0.08)]'
      } ${className}`}
    >
      {listening ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}
